import { useEffect, useState } from 'react';

import { CartInfoState } from '../CartInfoState';

import { CartInfoShipping, emptyCartInfoShipping, useCartShipping } from './useCartShipping';
import { CartInfoPayment, emptyCartInfoPayment, useCartPayment } from './useCartPayment';

import { getItemsByDeliveryType } from '.';

import { CartResponse } from 'common/api/e-comm/cart/lineitem/models/CartItems';
import { PaymentTypeFee } from 'common/api/e-comm/payment-types';
import { useRootSelector } from 'rootStore';
import { CartLineItem } from 'common/api/e-comm/models/CartLineItem';
import { ShippingOption } from 'common/api/e-comm/orders/service';
import { useShipDateEstimations } from 'common/hooks/useEstimatedShipDates';
import { PartialRecord } from 'common/definitions/PartialRecord';
import { APIResponse } from 'common/api/models';
import { ShipDateEstimation } from 'common/api/fulfillment/shipping/service';
import { ProductType } from 'common/api/e-comm/models/Product';

export const calculatePaymentFee = (total: number, paymentTypeFee?: PaymentTypeFee) => {
    if (!paymentTypeFee) {
        return 0;
    }

    if (paymentTypeFee.isPercentage) {
        return total * (paymentTypeFee.amount / 100);
    }

    return paymentTypeFee.amount;
};

export const calculateTotal = (
    cart: CartResponse | null,
    shippingFee: number,
    paymentFee: PaymentTypeFee | undefined
) => {
    const total = cart ? cart.subtotal + shippingFee : 0;

    if (total <= 0) {
        return 0;
    }

    return total + calculatePaymentFee(total, paymentFee);
};

export const useCartDetails = (cartInfo: CartInfoState): CartInfoDetails => {
    const { cart, cartAsyncState } = useRootSelector((s) => s.store.cart);

    const { loadShipDateEstimations, estimationResponses } = useShipDateEstimations();
    const [isLoadingShipDateEstimations, setIsLoadingShipDateEstimations] = useState(false);

    const payment = useCartPayment(cartInfo);
    const shipping = useCartShipping(cartInfo);

    const { shipToHome } = cartInfo;

    const { shippingFee, shippingOptions, isLoading: isLoadingShipping } = shipping;

    const { paymentFee, isLoading: isLoadingPayment } = payment;

    const total = calculateTotal(cart, shippingFee, paymentFee);

    const lineItems = cart?.lineItems || [];
    const hasShipToHomeEligibleItems = Boolean(
        lineItems.find(
            (item) =>
                item.product.type === ProductType.GiftCard && item.product.isShipToHomeEligible
        )
    );

    const { shipToHomeItems, shipToCoordinator, electronicDeliveryItems } = getItemsByDeliveryType(
        lineItems,
        Boolean(shipToHome)
    );

    const shipToHomeIfPossible = Boolean(shipToHome) && shipToHomeItems.length > 0;
    const cartSubtotal = cart?.subtotal || 0;

    useEffect(() => {
        (async () => {
            if (shippingOptions.length > 0) {
                try {
                    setIsLoadingShipDateEstimations(true);
                    await loadShipDateEstimations(shippingOptions, cartSubtotal);
                } finally {
                    setIsLoadingShipDateEstimations(false);
                }
            }
        })();
    }, [shippingOptions, cartSubtotal, loadShipDateEstimations]);

    return {
        isLoading: cartAsyncState.loading || isLoadingShipping || isLoadingPayment,
        cartSubtotal: cart?.subtotal || 0,
        total,
        shipping,
        payment,
        shipToHomeItems,
        shipToCoordinator,
        electronicDeliveryItems,
        hasShipToHomeEligibleItems,
        shipToHomeIfPossible,
        shippingOptions,
        isLoadingShipDateEstimations,
        estimationResponses,
    };
};

export interface CartInfoDetails {
    isLoading: boolean;
    cartSubtotal: number;
    total: number;
    shipping: CartInfoShipping;
    payment: CartInfoPayment;
    shipToHomeItems: CartLineItem[];
    shipToCoordinator: CartLineItem[];
    electronicDeliveryItems: CartLineItem[];
    hasShipToHomeEligibleItems: boolean;
    shipToHomeIfPossible: boolean;
    shippingOptions: ShippingOption[];
    isLoadingShipDateEstimations: boolean;
    estimationResponses: PartialRecord<string, APIResponse<ShipDateEstimation, null>>;
}

export const emptyCartInfoDetails: CartInfoDetails = {
    isLoading: false,
    cartSubtotal: 0,
    total: 0,
    shipping: emptyCartInfoShipping,
    payment: emptyCartInfoPayment,
    shipToHomeItems: [],
    shipToCoordinator: [],
    electronicDeliveryItems: [],
    hasShipToHomeEligibleItems: false,
    shipToHomeIfPossible: false,
    shippingOptions: [],
    isLoadingShipDateEstimations: false,
    estimationResponses: {},
};
